// src/contexts/AuthContext.jsx
import React, {createContext, useContext, useEffect, useState} from "react";
import {supabase} from "../supabaseClient";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({children}) => {
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);

    // fetch session + role once, and revalidate on focus
    const getSessionAndRole = async () => {
        try {
            const {data, error} = await supabase.auth.getSession();
            if (error || !data.session) {
                setUser(null);
                setRole(null);
            } else {
                setUser(data.session.user);
                // fetch role
                const {data: roleRow, error: roleError} = await supabase.from("auth_user_with_role").select("role").eq("id", data.session.user.id).maybeSingle();
                setRole(roleError ? null : roleRow?.role ?? null);
            }
        } catch (err) {
            console.error("getSession failed:", err);
            setUser(null);
            setRole(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // 1) kick off auto‑refresh loop
        supabase.auth.startAutoRefresh();

        // 2) initial load
        getSessionAndRole();

        // 3) revalidate on tab focus
        window.addEventListener("focus", getSessionAndRole);

        // 4) listen for token failures & sign‑out events
        const {data: listener} = supabase.auth.onAuthStateChange((event, session) => {
            console.log("Auth event:", event);
            if (event === "TOKEN_REFRESH_FAILED") {
                // refresh token expired → force sign‑out
                supabase.auth.signOut();
            }
            if (!session) {
                // any sign‑out or invalid session
                setUser(null);
                setRole(null);
                setLoading(false);
            }
        });

        return () => {
            listener.subscription.unsubscribe();
            window.removeEventListener("focus", getSessionAndRole);
        };
    }, []);

    const value = {user, role, loading};
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
